import { useState, useEffect } from 'react';
import { axiosClient } from '@/lib/axiosClient';
import type { Period } from '@/services/adminPeriodService';
import { useConventionReport } from '@/services/useConventionReport';
import { SummarySection } from '@/components/private/admin/convention-report/SummarySection';
import { ParticipantSection } from '@/components/private/admin/convention-report/ParticipantSection';
import { GuestSection } from '@/components/private/admin/convention-report/GuestSection';
import { ParticipantTable } from '@/components/private/admin/convention-report/ParticipantTable';
import { GuestTable } from '@/components/private/admin/convention-report/GuestTable';
import { ExportButtons } from '@/components/private/admin/convention-report/ExportButtons';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCcw, PieChart as PieChartIcon, Users, CheckCircle, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ConventionReportPage() {
  const [activeTab, setActiveTab] = useState('summary');
  const [guestCategoryId, setGuestCategoryId] = useState<string | undefined>(undefined);
  const [participantPackageId, setParticipantPackageId] = useState<string | undefined>(undefined);
  const [participantSearch, setParticipantSearch] = useState<string | undefined>(undefined);
  const [guestSearch, setGuestSearch] = useState<string | undefined>(undefined);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number | ''>('');
  
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const { data } = await axiosClient.get('/periods');
        // The API returns an array directly, so data is the array
        const periodsData = Array.isArray(data) ? data : (data?.data || []);
        setPeriods(periodsData);
        
        if (periodsData.length > 0) {
          // Find the active period
          const activePeriod = periodsData.find((p: Period) => p.is_active);
          if (activePeriod) {
            setSelectedPeriod(activePeriod.period_id);
          } else {
            setSelectedPeriod(periodsData[0].period_id);
          }
        }
      } catch (error) {
        console.error('Failed to load periods', error);
      }
    };
    fetchPeriods();
  }, []);

  const { data, isLoading, refetch } = useConventionReport(selectedPeriod as number);

  const handleCategoryClick = (categoryId: string) => {
    setGuestCategoryId(categoryId);
    setTimeout(() => {
      document.getElementById('guest-table-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handlePackageClick = (packageId: string) => {
    setParticipantPackageId(packageId);
    setActiveTab('participants');
    setTimeout(() => {
      document.getElementById('participant-table-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInstitutionClick = (institution: string) => {
    setParticipantSearch(institution);
    setActiveTab('participants');
    setTimeout(() => {
      document.getElementById('participant-table-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleGuestInstitutionClick = (institution: string) => {
    setGuestSearch(institution);
    setActiveTab('guests');
    setTimeout(() => {
      document.getElementById('guest-table-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    document.getElementById('report-tabs')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-24 px-4 sm:px-0">
      {/* HEADER SECTION DENGAN LATAR BELAKANG ELEGAN */}
      <div className="relative rounded-3xl overflow-hidden shadow-lg border border-slate-100 dark:border-slate-800">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-700 opacity-90" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20 mix-blend-overlay" />
        
        <div className="relative z-10 p-6 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="text-white w-full">
            <h1 className="text-3xl font-black mb-2 flex items-center gap-3">
              <PieChartIcon className="w-8 h-8 text-blue-200" />
              Laporan Konvensi
            </h1>
            <p className="text-blue-100/80 font-medium max-w-xl">
              Pantau statistik pendaftaran, detail tamu, dan pendapatan secara real-time berdasarkan periode konvensi.
            </p>
          </div>
        </div>
      </div>

      {/* FILTER & TOOLBAR SECTION */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <label className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
            Periode:
          </label>
          <Select 
            value={selectedPeriod ? selectedPeriod.toString() : ''} 
            onValueChange={(val) => {
              setSelectedPeriod(Number(val));
              setGuestCategoryId(undefined);
              setParticipantPackageId(undefined);
            }}
          >
            <SelectTrigger className="w-full sm:w-[250px] bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 font-semibold h-11">
              <SelectValue placeholder="Pilih Periode" />
            </SelectTrigger>
            <SelectContent>
              {periods.map(p => (
                <SelectItem key={p.period_id} value={p.period_id.toString()}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" className="h-11 px-4" onClick={() => refetch()} title="Refresh Data">
            <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {selectedPeriod && (
            <ExportButtons 
              periodId={selectedPeriod as number} 
              institutions={data?.participants?.per_institution}
            />
          )}
        </div>
      </div>

      {!selectedPeriod ? (
        <div className="p-8 text-center border rounded-lg bg-gray-50 text-gray-500">
          Silakan pilih periode terlebih dahulu.
        </div>
      ) : isLoading ? (
        <div className="p-12 text-center">Memuat laporan...</div>
      ) : !data ? (
        <div className="p-8 text-center border rounded-lg bg-red-50 text-red-500">
          Gagal memuat data laporan.
        </div>
      ) : (
        <div className="space-y-6">
          {/* TOTAL GRAND OVERVIEW */}
          <div className="grid grid-cols-1 mb-8">
            <div className="bg-gradient-to-br from-white via-slate-50 to-indigo-50/50 dark:from-slate-900 dark:via-slate-900/90 dark:to-indigo-950/30 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm relative overflow-hidden group">
              
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-500/5 dark:to-purple-500/5 blur-3xl group-hover:scale-110 transition-transform duration-700 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-gradient-to-tr from-blue-500/10 to-emerald-500/10 dark:from-blue-500/5 dark:to-emerald-500/5 blur-3xl group-hover:scale-110 transition-transform duration-700 pointer-events-none delay-75"></div>

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-200/50 dark:divide-slate-800">
                {/* Total Keseluruhan */}
                <div className="flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                      <Users className="w-4 h-4" />
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 font-semibold tracking-wide text-xs uppercase">Total Konvensi</p>
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-black bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-500 dark:from-white dark:to-slate-400 mt-1">
                    {data.participants.paid + data.event_guests.total} <span className="text-lg font-medium text-slate-500 dark:text-slate-500">Orang</span>
                  </h2>
                  <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-4 flex items-center gap-1.5 bg-white/60 dark:bg-slate-800/50 w-fit px-3 py-1.5 rounded-full border border-slate-200/60 dark:border-slate-700/50 shadow-sm backdrop-blur-sm">
                    <CheckCircle className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                    <span>Mencakup peserta lunas dan keseluruhan tamu</span>
                  </p>
                </div>

                {/* Total Peserta Lunas */}
                <div className="flex flex-col justify-center lg:pl-12 pt-6 lg:pt-0">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <p className="text-emerald-600 dark:text-emerald-500 font-semibold tracking-wide text-xs uppercase">Peserta Terverifikasi</p>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 dark:text-slate-100 mt-1">
                    {data.participants.paid} <span className="text-base font-medium text-slate-500 dark:text-slate-500">Orang</span>
                  </h2>
                </div>

                {/* Total Tamu & Panitia */}
                <div className="flex flex-col justify-center lg:pl-12 pt-6 lg:pt-0">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                      <Ticket className="w-4 h-4" />
                    </div>
                    <p className="text-blue-600 dark:text-blue-500 font-semibold tracking-wide text-xs uppercase">Tamu & Panitia</p>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 dark:text-slate-100 mt-1">
                    {data.event_guests.total} <span className="text-base font-medium text-slate-500 dark:text-slate-500">Orang</span>
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <Tabs id="report-tabs" value={activeTab} onValueChange={handleTabChange} className="w-full relative scroll-mt-28">
          <div className="sticky top-24 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-1.5 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 mb-6 w-full overflow-x-auto transition-all">
            <TabsList className="w-full flex justify-start bg-transparent h-auto p-0 space-x-1">
              <TabsTrigger value="summary" className="data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-900/30 data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-400 text-slate-600 dark:text-slate-400 rounded-lg py-2.5 px-6 font-medium transition-all">Ringkasan KPI</TabsTrigger>
              <TabsTrigger value="participants" className="data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-900/30 data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-400 text-slate-600 dark:text-slate-400 rounded-lg py-2.5 px-6 font-medium transition-all">Data Peserta</TabsTrigger>
              <TabsTrigger value="guests" className="data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-900/30 data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-400 text-slate-600 dark:text-slate-400 rounded-lg py-2.5 px-6 font-medium transition-all">Data Tamu & Panitia</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="summary" className="space-y-6 animate-in fade-in-50 duration-500">
            <SummarySection data={data} onInstitutionClick={handleInstitutionClick} onGuestInstitutionClick={handleGuestInstitutionClick} />
          </TabsContent>
          
          <TabsContent value="participants" className="space-y-12 animate-in fade-in-50 duration-500">
            <section>
              <ParticipantSection data={data} onPackageClick={handlePackageClick} />
            </section>
            
            <hr className="border-slate-200 dark:border-slate-800" />
            
            <section id="participant-table-section" className="scroll-mt-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900">Rincian Data Peserta</h3>
                <p className="text-sm text-slate-500">Gunakan filter di bawah ini untuk mencari data spesifik.</p>
              </div>
              <ParticipantTable 
                periodId={selectedPeriod as number} 
                defaultPackageId={participantPackageId}
                defaultSearch={participantSearch}
                packages={data.participants.per_package} 
              />
            </section>
          </TabsContent>
          
          <TabsContent value="guests" className="space-y-12 animate-in fade-in-50 duration-500">
            <section>
              <GuestSection data={data} onCategoryClick={handleCategoryClick} />
            </section>
            
            <hr className="border-slate-200 dark:border-slate-800" />

            <section id="guest-table-section" className="scroll-mt-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900">Rincian Data Tamu & Panitia</h3>
                <p className="text-sm text-slate-500">Gunakan filter di bawah ini untuk mencari data spesifik.</p>
              </div>
              <GuestTable 
                periodId={selectedPeriod as number} 
                defaultCategoryId={guestCategoryId} 
                defaultSearch={guestSearch}
                categories={data.event_guests.per_category}
              />
            </section>
          </TabsContent>
        </Tabs>
        </div>
      )}

      {/* FLOATING ACTION BUTTON (FAB) FOR EXPORT */}
      {selectedPeriod && data && (
        <div className="fixed bottom-6 right-6 z-50 shadow-2xl rounded-full">
          <ExportButtons 
            periodId={selectedPeriod as number} 
            isFab={true} 
            institutions={data.participants?.per_institution}
          />
        </div>
      )}
    </div>
  );
}
